<!-- Базовая карточка элемента настроек с действиями и опциональным основным кликом. -->
<template>
  <li
    class="settings-list-item-card card"
    :class="{
      'settings-list-item-card--disabled': !enabled,
      'settings-list-item-card--clickable': canRunPrimaryAction,
      'settings-list-item-card--compact-meta': compactMeta,
    }"
    :role="canRunPrimaryAction ? 'button' : null"
    :tabindex="canRunPrimaryAction ? 0 : null"
    @click="handlePrimaryAction"
    @keydown.enter.prevent="handlePrimaryAction"
    @keydown.space.prevent="handlePrimaryAction"
  >
    <template v-if="isEditing">
      <h4 v-if="editTitle" class="form-title">{{ editTitle }}</h4>
      <slot name="edit" />
    </template>

    <template v-else>
      <div class="settings-list-item-card__main">
        <div class="settings-list-item-card__info">
          <slot />
        </div>

        <div class="settings-list-item-card__actions" @click.stop>
          <slot v-if="!actionsAfterBuiltIn" name="actions" />

          <BaseViewIconButton v-if="showView" @click="$emit('view')" />

          <button
            v-if="showEdit"
            class="settings-list-item-card__action-btn settings-list-item-card__action-btn--edit"
            type="button"
            title="Редактировать"
            @click="$emit('edit')"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
              />
              <path
                d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
              />
            </svg>
          </button>

          <button
            v-if="showDelete"
            class="settings-list-item-card__action-btn settings-list-item-card__action-btn--delete"
            type="button"
            title="Удалить"
            @click="showDeleteConfirm = true"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="3 6 5 6 21 6" />
              <path
                d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"
              />
              <path d="M10 11v6M14 11v6" />
              <path
                d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"
              />
            </svg>
          </button>

          <div v-if="showToggle" class="settings-list-item-card__toggle">
            <base-toggle-switch
              :model-value="enabled"
              :disabled="isLoading"
              @update:model-value="$emit('toggle')"
            />
          </div>

          <slot v-if="actionsAfterBuiltIn" name="actions" />
        </div>
      </div>

      <div
        v-if="$slots.details"
        class="settings-list-item-card__details"
      >
        <slot name="details" />
      </div>

      <div
        v-if="createdAt || updatedAt"
        class="settings-list-item-card__meta"
      >
        <MetaTime
          :created-at="createdAt"
          :updated-at="updatedAt"
          :compact="compactMeta"
        />
      </div>
    </template>

    <Teleport to="body">
      <base-dialog
        :visible="showDeleteConfirm"
        title="Подтвердите удаление"
        :message="deleteMessage"
        :actions="[
          { key: 'confirm', label: 'Удалить', type: 'danger' },
          { key: 'close', label: 'Отмена', type: 'secondary' },
        ]"
        :close-on-overlay="true"
        @action="onDeleteDialogAction"
      />
    </Teleport>
  </li>
</template>

<script>
import MetaTime from "@/components/Meta/MetaTime.vue";
import { isSettingsCardInteractiveTarget } from "@/utils/settingsListItemCardUtils";

export default {
  name: "BaseSettingsListItemCard",

  components: {
    MetaTime,
  },

  props: {
    enabled: {
      type: Boolean,
      default: true,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    editTitle: {
      type: String,
      default: "",
    },
    createdAt: {
      type: String,
      default: null,
    },
    updatedAt: {
      type: String,
      default: null,
    },
    showView: {
      type: Boolean,
      default: false,
    },
    showEdit: {
      type: Boolean,
      default: true,
    },
    showToggle: {
      type: Boolean,
      default: true,
    },
    showDelete: {
      type: Boolean,
      default: false,
    },
    deleteMessage: {
      type: String,
      default: "Запись будет удалена безвозвратно.",
    },
    clickable: {
      type: Boolean,
      default: false,
    },
    compactMeta: {
      type: Boolean,
      default: true,
    },
    actionsAfterBuiltIn: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["view", "edit", "toggle", "delete", "primary-action"],

  data() {
    return {
      showDeleteConfirm: false,
    };
  },

  computed: {
    canRunPrimaryAction() {
      return this.clickable && !this.isEditing;
    },
  },

  methods: {
    handlePrimaryAction(event) {
      if (!this.canRunPrimaryAction || this.isInteractiveTarget(event.target)) {
        return;
      }

      this.$emit("primary-action");
    },

    isInteractiveTarget(target) {
      return isSettingsCardInteractiveTarget(
        target,
        this.$el
      );
    },

    onDeleteDialogAction(actionKey) {
      if (actionKey === "confirm") this.$emit("delete");
      this.showDeleteConfirm = false;
    },
  },
};
</script>

<style scoped>
.settings-list-item-card {
  padding: 16px 20px;
}

.settings-list-item-card--disabled {
  background-color: rgba(186, 186, 186, 0.35);
}

.settings-list-item-card--clickable {
  cursor: pointer;
}

.settings-list-item-card--clickable:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 4px;
}

.form-title {
  margin-bottom: var(--base-in-indent);
}

.settings-list-item-card__main {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.settings-list-item-card__info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.settings-list-item-card__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.settings-list-item-card__toggle {
  display: flex;
  align-items: center;
}

.settings-list-item-card__action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 35px;
  height: 35px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: #6b7280;
  background: none;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.settings-list-item-card__action-btn--edit:hover {
  background: #dbeafe;
  color: var(--color-blue);
}

.settings-list-item-card__action-btn--delete:hover {
  background: #fee2e2;
  color: var(--color-danger);
}

.settings-list-item-card__meta {
  margin-top: var(--base-in-indent);
}

.settings-list-item-card--compact-meta .settings-list-item-card__meta {
  margin-top: 6px;
}

.settings-list-item-card__details {
  margin-top: 14px;
}

@media (max-width: 600px) {
  .settings-list-item-card__main {
    flex-direction: column;
  }

  .settings-list-item-card__actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
